import React, { useMemo, useState } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import DrawerShell from '../Receiving/DrawerShell';
import SectionChip from '../components/SectionChip';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Field } from '../components/ui/Field';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import { AREA_CODE_RE, MAX_COLUMNS, MAX_ROWS, MAX_SECTIONS, sectionCodes } from './sectionGrid';
import type { LocationAdminRow } from './LocationsAdmin';

const LABEL = 'mb-1 block text-xs text-on-surface-variant';
const PREVIEW_CODES = 6;

type Props =
  | { mode: 'add'; parent: LocationAdminRow; onClose: () => void; onDone: () => void }
  | {
      mode: 'grow';
      area: LocationAdminRow;
      /** Current extent, and how many section rows the grid already holds (retired ones included). */
      columns: number;
      rows: number;
      existing: number;
      onClose: () => void;
      onDone: () => void;
    };

const intOf = (s: string) => (/^\d+$/.test(s.trim()) ? parseInt(s, 10) : NaN);

/** Add an area to an inventory location, or grow an area's grid. The server stays the authority; the preview and the 500 guard run first. */
const AreaGridForm: React.FC<Props> = (props) => {
  const api = useApi();
  const toast = useToast();
  const grow = props.mode === 'grow';
  const areaCode = grow ? props.area.code || '' : '';

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [cols, setCols] = useState(grow ? String(props.columns) : '');
  const [rows, setRows] = useState(grow ? String(props.rows) : '');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const columns = intOf(cols);
  const rowCount = intOf(rows);
  const dimsOk =
    columns >= 1 && columns <= MAX_COLUMNS && rowCount >= 1 && rowCount <= MAX_ROWS;
  const total = dimsOk ? columns * rowCount : 0;
  // On a grow only the missing sections count toward the guard, as on the server.
  const created = grow ? Math.max(0, total - props.existing) : total;
  const shrinks = grow && dimsOk && (columns < props.columns || rowCount < props.rows);
  const tooBig = dimsOk && created > MAX_SECTIONS;
  const codeValue = grow ? areaCode : code;
  const codeOk = grow || AREA_CODE_RE.test(code);

  const preview = useMemo(
    () => (dimsOk && codeOk && !tooBig ? sectionCodes(codeValue, columns, rowCount, PREVIEW_CODES) : []),
    [dimsOk, codeOk, tooBig, codeValue, columns, rowCount],
  );

  const problem = !dimsOk
    ? columns > MAX_COLUMNS || rowCount > MAX_ROWS
      ? `Columns go up to ${MAX_COLUMNS} and rows up to ${MAX_ROWS}.`
      : null
    : shrinks
      ? `A grid cannot shrink. Keep at least ${props.mode === 'grow' ? props.columns : 1} columns and ${props.mode === 'grow' ? props.rows : 1} rows.`
      : tooBig
        ? `That makes ${created} new sections. One area takes up to ${MAX_SECTIONS} at a time, so make the grid smaller.`
        : grow && created === 0
          ? 'This is the grid it already has. Add a column or a row to grow it.'
          : null;

  const canSubmit =
    !saving && dimsOk && codeOk && !problem && (grow || name.trim().length > 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setServerError(null);
    try {
      if (props.mode === 'add') {
        await api.post('/locations/areas', {
          body: { parent_location_id: props.parent.id, name: name.trim(), code, columns, rows: rowCount },
        });
        toast.success(`${name.trim()} added with ${total} sections.`);
      } else {
        await api.post(`/locations/${props.area.id}/grow`, { body: { columns, rows: rowCount } });
        toast.success(`${props.area.name} grew by ${created} ${created === 1 ? 'section' : 'sections'}.`);
      }
      props.onDone();
    } catch (err: any) {
      setServerError(err?.data?.error?.message || err?.message || 'The grid did not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const dirty = grow
    ? cols !== String(props.columns) || rows !== String(props.rows)
    : name.trim() !== '' || code !== '' || cols !== '' || rows !== '';

  const title = grow ? `Grow ${props.area.name}` : `Add area to ${props.parent.name}`;
  const shown = Math.min(created, PREVIEW_CODES);
  const more = created - shown;

  return (
    <DrawerShell title={title} onClose={props.onClose} dirty={dirty}>
      <form onSubmit={submit} className="space-y-4">
        {!grow && (
          <>
            <label className="block">
              <span className={LABEL}>Name</span>
              <Field
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Pallet rack"
                autoComplete="off"
                data-drawer-autofocus
              />
            </label>
            <label className="block">
              <span className={LABEL}>Code, 2 to 5 letters or digits</span>
              <Field
                className="font-mono uppercase"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5))}
                placeholder="PLLT"
                autoComplete="off"
                spellCheck={false}
              />
              <span className="mt-1 block text-xs text-on-surface-variant">
                The code is printed on every label, so it cannot be changed later.
              </span>
            </label>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={LABEL}>Columns</span>
            <Field
              inputMode="numeric"
              value={cols}
              onChange={(e) => setCols(e.target.value.replace(/\D/g, ''))}
              placeholder="4"
              autoComplete="off"
              data-drawer-autofocus={grow ? '' : undefined}
            />
          </label>
          <label className="block">
            <span className={LABEL}>Rows</span>
            <Field
              inputMode="numeric"
              value={rows}
              onChange={(e) => setRows(e.target.value.replace(/\D/g, ''))}
              placeholder="5"
              autoComplete="off"
            />
          </label>
        </div>
        {grow && (
          <p className="text-xs text-on-surface-variant">
            Now {props.columns} by {props.rows}. Grids only grow, and a retired section stays retired.
          </p>
        )}

        <div role="status" aria-live="polite" className="rounded-md bg-surface-container-low p-3 text-sm">
          <FadeIn key={problem ? 'problem' : preview.length > 0 ? 'preview' : 'empty'}>
            {problem ? (
              <Alert tone="error" variant="text" live={false}>{problem}</Alert>
            ) : preview.length > 0 ? (
              <>
                <p className="mb-2 tabular-nums text-on-surface">
                  {grow
                    ? `${created} new ${created === 1 ? 'section' : 'sections'}, ${total} in all`
                    : `${total} ${total === 1 ? 'section' : 'sections'}`}
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {preview.map((c) => (
                    <SectionChip key={c} code={c} />
                  ))}
                  {more > 0 && <span className="text-xs tabular-nums text-on-surface-variant">and {more} more</span>}
                </div>
              </>
            ) : (
              <p className="text-on-surface-variant">
                {grow ? 'Set the new size to see the sections it adds.' : 'Name the area and size the grid to see its section codes.'}
              </p>
            )}
          </FadeIn>
        </div>

        <Reveal show={!!serverError}>
          <Alert tone="error" variant="text">{serverError}</Alert>
        </Reveal>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={props.onClose} className={buttonClass('quiet')}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit} className={buttonClass('filled')}>
            {saving ? 'Saving…' : grow ? 'Grow grid' : 'Add area'}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
};

export default AreaGridForm;
